import { Box, type SxProps, type Theme, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import type { DateTime } from "luxon";
import { type FC, type MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";

type Meridiem = "AM" | "PM";

type Props = {
	/** The selected time as a Luxon `DateTime`, or `null` when unset. Only the hour/minute are meaningful. */
	value: DateTime | null;
	/** Fires with the new time (or `null` when cleared) — on EVERY section the user fills. */
	onChange: (value: DateTime | null) => void;
	/**
	 * Fires once the user has FINISHED picking — the action bar's OK, or the last section completing.
	 *
	 * Use this, not `onChange`, for anything that consumes the value and moves on (appending to a list,
	 * closing a row). `onChange` emits a usable DateTime the moment the hour is chosen, while the
	 * meridiem is still unset, so committing there silently turns every intended PM time into AM.
	 */
	onAccept?: (value: DateTime | null) => void;
	label?: string;
	disabled?: boolean;
	/** Minute granularity of the wheel/keyboard entry (default 5). */
	minutesStep?: number;
	/** 12h AM/PM vs 24h. Default 12h to match the app's shift-time display. */
	ampm?: boolean;
	/**
	 * Render AM/PM as an explicit segmented toggle instead of a typed field section: the hour/minute
	 * stays free text (`hh:mm`) and the meridiem is chosen from AM/PM buttons. Implies 12h; ignored when
	 * `ampm` is false.
	 */
	meridiemToggle?: boolean;
	/**
	 * Focus the field's first section on mount. For list-style callers that remount the picker after
	 * each entry, this is what returns the cursor for the next one instead of forcing a re-click.
	 */
	autoFocus?: boolean;
	/**
	 * Pick only, never type: the field is read-only and a tap anywhere on it opens the clock in a dialog. For a phone
	 * sheet, where MUI's responsive picker keeps the field typeable and a tap brings up the keyboard. `onChange` then
	 * fires once per pick, on the dialog's OK (Cancel changes nothing). Ignores `meridiemToggle` (the clock has its own
	 * AM/PM).
	 */
	openOnTap?: boolean;
	fullWidth?: boolean;
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

/** AM/PM of a time, or null when unset (nothing to preselect on the toggle). Exported for unit tests. */
export const meridiemOf = (value: DateTime | null): Meridiem | null => (value ? (value.hour < 12 ? "AM" : "PM") : null);

/** Move a time into the chosen half-day, preserving the 12h hour + minute. Exported for unit tests. */
export const withMeridiem = (value: DateTime, meridiem: Meridiem): DateTime => {
	const isPM = value.hour >= 12;
	if (meridiem === "PM" && !isPM) return value.plus({ hours: 12 });
	if (meridiem === "AM" && isPM) return value.minus({ hours: 12 });
	return value;
};

/**
 * Generic single-time picker — a thin, themed wrap of MUI's `TimePicker` on the Luxon adapter. The app
 * only had start+end *range* pickers (`TimeRangeSelection` / `ShiftHoursDisplayAndPicker`); this covers
 * the "pick one time" case (e.g. an interview hour). Self-contained `LocalizationProvider` so callers
 * don't need to wrap it. Prop-driven, tokenized (no hardcoded colors), keyboard-accessible via MUI.
 *
 * With `meridiemToggle`, the meridiem moves out of the typed field into an AM/PM segmented control so
 * the user free-types only the hour/minute and picks the half-day from constrained buttons.
 */
export const SystemTimePicker: FC<Props> = ({
	value,
	onChange,
	onAccept,
	label,
	disabled,
	minutesStep = 5,
	ampm = true,
	meridiemToggle = false,
	autoFocus = false,
	// DEFAULT: off — every existing caller keeps the typeable field.
	openOnTap = false,
	fullWidth = true,
	sx,
	dataTestId,
}) => {
	const { t } = useTranslation();
	const [isTapPickerOpen, setIsTapPickerOpen] = useState(false);
	const useToggle = meridiemToggle && ampm && !openOnTap;

	if (openOnTap) {
		return (
			<LocalizationProvider dateAdapter={AdapterLuxon}>
				<Box data-testid={dataTestId} sx={[{ width: fullWidth ? "100%" : "auto" }, ...(Array.isArray(sx) ? sx : [sx])]}>
					<MobileTimePicker
						// Uncontrolled, and remounted whenever the value moves from outside (a chip, a linked field): the
						// picker keeps its own draft while the clock is open, and its Cancel returns to the value it was
						// mounted with — the one on screen. Controlled, it had nowhere to keep the draft, and it reverted to
						// the value it last committed itself, which a change made outside it never updates.
						key={value === null ? "unset" : value.toMillis()}
						defaultValue={value}
						// Only a finished pick reaches the caller: the field cannot be typed into, and the clock's own
						// steps (hour, then minute) are a draft until OK.
						onAccept={(accepted) => {
							onChange(accepted);
							onAccept?.(accepted);
						}}
						label={label}
						disabled={disabled}
						minutesStep={minutesStep}
						ampm={ampm}
						open={isTapPickerOpen}
						onOpen={() => setIsTapPickerOpen(true)}
						onClose={() => setIsTapPickerOpen(false)}
						slotProps={{
							textField: { fullWidth, size: "small", onClick: () => !disabled && setIsTapPickerOpen(true) },
							field: { readOnly: true },
						}}
					/>
				</Box>
			</LocalizationProvider>
		);
	}

	const handleMeridiem = (_event: MouseEvent<HTMLElement>, next: Meridiem | null) => {
		// A null `next` is the toggle deselecting the active button — ignore it (a time always has a
		// meridiem). With no time typed yet there's nothing to move, so wait for the hour/minute first.
		if (next && value) onChange(withMeridiem(value, next));
	};

	return (
		<LocalizationProvider dateAdapter={AdapterLuxon}>
			{/* Wrapper carries the test id — the strict picker text-field slot type doesn't enumerate data-testid. */}
			<Box
				data-testid={dataTestId}
				sx={[
					useToggle ? { display: "flex", gap: 1, alignItems: "center", width: fullWidth ? "100%" : "auto" } : { width: fullWidth ? "100%" : "auto" },
					...(Array.isArray(sx) ? sx : [sx]),
				]}
			>
				<TimePicker
					value={value}
					onChange={onChange}
					onAccept={onAccept}
					label={label}
					disabled={disabled}
					minutesStep={minutesStep}
					ampm={ampm}
					// Drop the meridiem section from the typed field when the toggle owns it.
					{...(useToggle ? { format: "hh:mm", views: ["hours", "minutes"] as const } : {})}
					slotProps={{
						// `autoFocus` rides the textField slot because that is where the picker splits field
						// internal props back out — the root doesn't forward it.
						textField: { fullWidth, size: "small", autoFocus },
						field: { clearable: true, onClear: () => onChange(null) },
					}}
				/>
				{useToggle && (
					<ToggleButtonGroup
						exclusive
						size="small"
						disabled={disabled}
						value={meridiemOf(value)}
						onChange={handleMeridiem}
						aria-label={t("hiring_chat.interview_time.label")}
						sx={{ flexShrink: 0 }}
					>
						<ToggleButton value="AM" data-testid="time-meridiem-am" sx={{ textTransform: "none", px: 1.5 }}>
							{t("AM")}
						</ToggleButton>
						<ToggleButton value="PM" data-testid="time-meridiem-pm" sx={{ textTransform: "none", px: 1.5 }}>
							{t("PM")}
						</ToggleButton>
					</ToggleButtonGroup>
				)}
			</Box>
		</LocalizationProvider>
	);
};

export default SystemTimePicker;
